import { useEffect, useRef } from 'react';
import { LAYERS } from '@/utils/layers';

interface UseDialogAccessibilityProps {
  isOpen: boolean;
  onClose: () => void;
  ariaLabel?: string;
  ariaDescribedBy?: string;
}

const useDialogAccessibility = ({
  isOpen,
  onClose,
  ariaLabel,
  ariaDescribedBy
}: UseDialogAccessibilityProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Store current focus
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus dialog
      if (dialogRef.current) {
        dialogRef.current.focus();
      }

      // Prevent body scroll while preserving floating element interactions
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      // Ensure floating elements remain clickable
      const floatingElements = document.querySelectorAll(`[class*="z-[${LAYERS.ACTION_CNT}]"], [class*="z-[${LAYERS.FLOAT_BTN}]"], [class*="z-[${LAYERS.DIALOG_HDR}]"]`);
      floatingElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.style.pointerEvents = 'auto';
        }
      });
    } else {
      // Restore focus
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }

      // Restore body scroll and remove padding
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  // Focus trap implementation
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const dialog = dialogRef.current;
    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    dialog.addEventListener('keydown', handleTabKey);
    return () => dialog.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  return {
    dialogRef,
    dialogProps: {
      ref: dialogRef,
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      tabIndex: -1,
    },
  };
};

export default useDialogAccessibility;
export { useDialogAccessibility };
